import React, { useMemo } from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { ToastService } from '@iamsoftware/react-hooks';

interface Step2SignersProps {
    documentRecipients: any[];
    roleOptions: any[];
    isViewMode?: boolean;
    getCustomerSuggestionsForRow: (idx: number) => any[];
    searchCustomers: (e: any) => void;
    createCustomer: (idx: number) => void;
    handleRecipientChange: (idx: number, field: string, value: any) => void;
    addRecipient: () => void;
    removeRecipient: (idx: number) => void;
    getRoleOptionsForRow: (idx: number) => any[];
    isTemplateMode?: boolean;
    isSequential?: boolean;
}

export const Step2Signers: React.FC<Step2SignersProps> = ({
    documentRecipients,
    roleOptions,
    isViewMode = false,
    getCustomerSuggestionsForRow,
    searchCustomers,
    createCustomer,
    handleRecipientChange,
    addRecipient,
    removeRecipient,
    getRoleOptionsForRow,
    isTemplateMode = false,
    isSequential = false
}) => {
    // Mảng riêng quản lý các ID đã được chọn để xử lý logic unique
    const takenRoleIds = useMemo(() =>
        documentRecipients.map(r => r.roleTypeId).filter(Boolean),
        [documentRecipients]
    );

    const takenPartyIds = useMemo(() =>
        documentRecipients.map(r => r.partyId || r.party?.partyId || r.party?.value).filter(Boolean),
        [documentRecipients]
    );

    return (
        <div className="grid">
            <div className="col-12 flex justify-content-between align-items-center mb-3">
                <span className="text-xl font-bold">Danh sách người nhận</span>
                <div className="flex gap-2 align-items-center">
                    {!isViewMode && !isTemplateMode && (
                        <Button
                            icon="pi pi-plus"
                            label="Thêm người nhận"
                            size="small"
                            onClick={addRecipient}
                            disabled={documentRecipients.length >= roleOptions.length}
                        />
                    )}
                </div>
            </div>
            <div className="col-12">
                {documentRecipients.map((rec, idx) => {
                    // Lọc danh sách vai trò: ẩn các vai trò đã chọn ở dòng khác
                    const filteredRoleOptions = roleOptions.filter(
                        opt => !takenRoleIds.includes(opt.value) || opt.value === rec.roleTypeId
                    );

                    // Lọc danh sách gợi ý đối tượng: ẩn các đối tượng đã chọn ở dòng khác
                    const currentPartyId = rec.partyId || rec.party?.partyId || rec.party?.value;
                    const filteredSuggestions = getCustomerSuggestionsForRow(idx)
                        .map(c => {
                            const { disabled, ...rest } = c;
                            return rest;
                        })
                        .filter(c => !takenPartyIds.includes(c.partyId || c.value) || (c.partyId || c.value) === currentPartyId);

                    return (
                        <div className="card shadow-1 border-round-lg mb-3 p-3 border-1 surface-border" key={rec._internalId || idx}>
                            <div className="grid">
                                <div className="col-12 md:col-3 field">
                                    <label className="font-bold block mb-1">Đối tượng <span className="text-red-500">*</span></label>
                                    <div className="p-inputgroup">
                                        <AutoComplete
                                            dropdown
                                            field="label"
                                            suggestions={filteredSuggestions}
                                            value={rec.party}
                                            onChange={(e) => {
                                                const selectedValue = e.value;
                                                const partyId = selectedValue?.partyId || selectedValue?.value;
                                                
                                                if (partyId && takenPartyIds.includes(partyId) && partyId !== (rec.partyId || rec.party?.partyId || rec.party?.value)) {
                                                    ToastService.error('Đối tượng này đã được chọn ở dòng khác');
                                                    handleRecipientChange(idx, 'party', null);
                                                    return;
                                                }
                                                handleRecipientChange(idx, 'party', selectedValue);
                                            }}
                                            completeMethod={searchCustomers}
                                            placeholder="Tìm kiếm đối tượng"
                                            disabled={isViewMode}
                                            appendTo="self"
                                        />
                                        {!isViewMode && (
                                            <Button icon="pi pi-plus" severity="success" onClick={() => createCustomer(idx)} />
                                        )}
                                    </div>
                                </div>
                                <div className="col-12 md:col-3 field">
                                    <label className="font-bold block mb-1">Mã số thuế</label>
                                    <InputText
                                        value={rec.taxCode || ''}
                                        onChange={(e) => handleRecipientChange(idx, 'taxCode', e.target.value)}
                                        onInput={(e) => {
                                            const value = (e.target as HTMLInputElement).value.replace(/[^0-9-]/g, '');
                                            if (value !== (e.target as HTMLInputElement).value) {
                                                (e.target as HTMLInputElement).value = value;
                                                handleRecipientChange(idx, 'taxCode', value);
                                            }
                                        }}
                                        onPaste={(e) => {
                                            const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                                            if (!/^[0-9-]*$/.test(paste)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        maxLength={14}
                                        disabled={isViewMode}
                                        className="w-full"
                                    />
                                </div>
                                <div className="col-12 md:col-3 field">
                                    <label className="font-bold block mb-1">Tên công ty</label>
                                    <InputText
                                        value={rec.partyName || ''}
                                        onInput={(e) => {
                                            const inputEl = e.target as HTMLInputElement;
                                            const value = inputEl.value.replace(/[^a-zA-ZÀ-ỹ\s-]/g, '');
                                            if (value !== inputEl.value) inputEl.value = value;
                                        }}
                                        onChange={(e) => handleRecipientChange(idx, 'partyName', e.target.value)}
                                        pattern="[a-zA-ZÀ-ỹ\s-]+"
                                        title="Chỉ được nhập chữ cái và khoảng trắng"
                                        disabled={isViewMode}
                                        className="w-full"
                                    />
                                </div>
                                <div className="col-12 md:col-3 field">
                                    <label className="font-bold block mb-1">Email <span className="text-red-500">*</span></label>
                                    <InputText
                                        value={rec.emailAddress || ''}
                                        onChange={(e) => handleRecipientChange(idx, 'emailAddress', e.target.value)}
                                        disabled={isViewMode}
                                        className="w-full"
                                    />
                                </div>
                                <div className="col-12 md:col-3 field">
                                    <label className="font-bold block mb-1">Số điện thoại</label>
                                    <InputText
                                        value={rec.contactNumber || ''}
                                        onInput={(e) => {
                                            const value = (e.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
                                            (e.target as HTMLInputElement).value = value;
                                            handleRecipientChange(idx, 'contactNumber', value);
                                        }}
                                        onPaste={(e) => {
                                            const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                                            if (!/^[0-9]*$/.test(paste)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        maxLength={10}
                                        pattern="[0-9]*"
                                        inputMode="numeric"
                                        disabled={isViewMode}
                                        className="w-full"
                                    />
                                </div>
                                <div className="col-12 md:col-3 field">
                                    <label className="font-bold block mb-1">Họ và Tên</label>
                                    <InputText
                                        value={rec.representativeName || ''}
                                        onInput={(e) => {
                                            const inputEl = e.target as HTMLInputElement;
                                            const value = inputEl.value.replace(/[^a-zA-ZÀ-ỹ\s-]/g, '');
                                            if (value !== inputEl.value) inputEl.value = value;
                                        }}
                                        onChange={(e) => handleRecipientChange(idx, 'representativeName', e.target.value)}
                                        onPaste={(e) => {
                                            const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                                            if (/[^a-zA-ZÀ-ỹ\s-]/.test(paste)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        pattern="[a-zA-ZÀ-ỹ\s-]+"
                                        title="Chỉ được nhập chữ cái và khoảng trắng"
                                        disabled={isViewMode || rec.partyTypeEnumId === 'PtyPerson' || rec.party?.partyTypeEnumId === 'PtyPerson'}
                                        className="w-full"
                                    />
                                </div>
                                <div className="col-12 md:col-3 field">
                                    <label className="font-bold block mb-1">CCCD/CMND</label>
                                    <InputText
                                        value={rec.identityCard || ''}
                                        onInput={(e) => {
                                            const value = (e.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
                                            const limitedValue = value.slice(0, 12);
                                            (e.target as HTMLInputElement).value = limitedValue;
                                            handleRecipientChange(idx, 'identityCard', limitedValue);
                                        }}
                                        onPaste={(e) => {
                                            const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                                            if (!/^[0-9]*$/.test(paste)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        maxLength={12}
                                        pattern="[0-9]*"
                                        inputMode="numeric"
                                        disabled={isViewMode || rec.partyTypeEnumId === 'PtyOrganization' || rec.party?.partyTypeEnumId === 'PtyOrganization'}
                                        className="w-full"
                                    />
                                </div>
                                <div className="col-12 md:col-3 field">
                                    <label className="font-bold block mb-1">Vai trò <span className="text-red-500">*</span></label>
                                    <Dropdown
                                        options={filteredRoleOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        value={rec.roleTypeId}
                                        onChange={(e) => handleRecipientChange(idx, 'roleTypeId', e.value)}
                                        disabled={isViewMode || isTemplateMode}
                                        className="w-full"
                                    />
                                </div>
                                {/* Tạm ẩn thứ tự theo yêu cầu đại ca
                                {isSequential && (
                                    <div className="col-12 md:col-1 field">
                                        <label className="font-bold block mb-1">Thứ tự</label>
                                        <InputText
                                            type="number"
                                            value={rec.signingOrder || (idx + 1)}
                                            onChange={(e) => handleRecipientChange(idx, 'signingOrder', parseInt(e.target.value) || 1)}
                                            min={1}
                                            disabled={isViewMode}
                                            className="w-full"
                                        />
                                    </div>
                                )} */}
                            </div>
                            {!isViewMode && !isTemplateMode && (
                                <div className="flex justify-content-end mt-2">
                                    <Button
                                        icon="pi pi-trash"
                                        label="Xóa người nhận"
                                        severity="danger"
                                        text
                                        size="small"
                                        onClick={() => removeRecipient(idx)}
                                        disabled={documentRecipients.length === 1}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
